import { hmac } from "@noble/hashes/hmac.js";
import { sha1 } from "@noble/hashes/legacy.js";
import { sha256, sha512 } from "@noble/hashes/sha2.js";

export type Algorithm = "SHA1" | "SHA256" | "SHA512";

// The three HMACs every one of the three modes is built on: RFC 4226 names SHA-1, and RFC 6238 and RFC 6287 add the
// other two. Nothing here reaches Web Crypto, which only answers asynchronously and would make a code that ticks once
// a second a promise the page has to hold rather than a value it renders.
export const HASHES = { SHA1: sha1, SHA256: sha256, SHA512: sha512 };

export const ALGORITHMS = Object.keys(HASHES) as Algorithm[];

// RFC 4226's HOTP, which TOTP is the same function of a time step and OCRA the same function of a longer message.
export function hotp(key: Uint8Array, counter: bigint, algorithm: Algorithm, digits: number): string {
  return truncate(hmac(HASHES[algorithm], key, counterBytes(counter)), digits);
}

// The step a moment falls in, which is the whole of what TOTP adds to HOTP. Floored, so a negative epoch second lands
// in the step before the epoch rather than the one after it.
export function timeStep(seconds: number, period: number): bigint {
  return BigInt(Math.floor(seconds / period));
}

// An unsigned 8-byte counter, high-order bit first, which is C in HOTP and both C and T in OCRA.
export function counterBytes(counter: bigint): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(8);
  let value = BigInt.asUintN(64, counter);
  for (let index = 7; index >= 0; index--) {
    bytes[index] = Number(value & 0xffn);
    value >>= 8n;
  }
  return bytes;
}

// RFC 4226's dynamic truncation: the low nibble of the last byte says where to read the four that carry the code, and
// the top bit of those is dropped so the same digits come out of a signed reading of them. A digit count of 0 is
// OCRA's "no truncation at all", where the whole HMAC is the answer and there is nothing decimal about it.
export function truncate(mac: Uint8Array, digits: number): string {
  if (digits === 0) return Array.from(mac, (byte) => byte.toString(16).padStart(2, "0")).join("");
  const offset = mac[mac.length - 1] & 0x0f;
  const binary = ((mac[offset] & 0x7f) << 24)
    | (mac[offset + 1] << 16)
    | (mac[offset + 2] << 8)
    | mac[offset + 3];
  return String(binary % 10 ** digits).padStart(digits, "0");
}
